import { useEffect } from "react";
import Lenis from "lenis";
import { useQuery } from "@tanstack/react-query";
import { BrowserRouter, Link, Route, Routes, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Toaster } from "sonner";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { HomePage } from "@/components/site/HomePage";
import { AdminDashboard } from "@/components/site/AdminDashboard";
import { api } from "@/lib/api";
import { BUSINESS, LOCATION_PAGES, SERVICE_PAGES } from "@/data/siteContent";

const getRotationStyle = (rotation = 0, scale = 1.08) =>
  rotation
    ? {
        transform: `rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: "center center",
      }
    : undefined;

const useMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    const tag = document.querySelector('meta[name="description"]') || document.createElement("meta");
    tag.setAttribute("name", "description");
    tag.setAttribute("content", description);
    if (!document.head.contains(tag)) {
      document.head.appendChild(tag);
    }
  }, [title, description]);
};

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const LoadingScreen = () => (
  <div className="page-shell loading-shell" data-testid="app-loading-screen">
    <div className="loading-mark">GTA Modern</div>
    <p data-testid="app-loading-copy">Loading Toronto's most cinematic quote funnel…</p>
  </div>
);

const DetailPage = ({ siteData, type }) => {
  const { slug } = useParams();

  const entry =
    type === "service"
      ? SERVICE_PAGES.find((item) => item.slug === slug)
      : type === "location"
        ? LOCATION_PAGES.find((item) => item.slug === slug)
        : siteData?.blog_posts?.find((item) => item.slug === slug);

  const title =
    type === "blog"
      ? `${entry?.title || "Painting insights"} | ${BUSINESS.name}`
      : `${entry?.title || entry?.name || entry?.city || "Painting services"} | ${BUSINESS.name}`;

  const description = entry?.summary || entry?.excerpt || entry?.body || BUSINESS.coverage;
  useMeta(title, description);

  if (!entry) {
    return (
      <div className="detail-page" data-testid="detail-page-not-found">
        <div className="detail-card">
          <p className="detail-eyebrow">Not found</p>
          <h1>The page you wanted is still drying.</h1>
          <Link to="/" data-testid="detail-back-home-link">
            <Button className="rounded-full">Back home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="detail-page" data-testid={`${type}-detail-page`}>
      <div className="detail-card">
        <Link to="/" className="detail-back-link" data-testid={`${type}-detail-back-link`}>
          <ArrowLeft size={16} /> Back to home
        </Link>
        <p className="detail-eyebrow" data-testid={`${type}-detail-eyebrow`}>
          {entry.eyebrow || `Location / ${entry.city}`}
        </p>
        <h1 data-testid={`${type}-detail-title`}>{entry.name || entry.title}</h1>
        <p className="detail-lead" data-testid={`${type}-detail-description`}>
          {entry.summary || entry.excerpt || entry.body}
        </p>
        {entry.image || entry.hero_image ? (
          <div className="detail-image-shell" data-testid={`${type}-detail-image-shell`}>
            <img
              src={entry.image || entry.hero_image}
              alt={entry.name || entry.title}
              className="detail-image"
              style={type === "service" ? getRotationStyle(entry.rotation, 1.08) : undefined}
              data-testid={`${type}-detail-image`}
            />
          </div>
        ) : null}
        {entry.bullets ? (
          <div className="detail-bullet-grid" data-testid={`${type}-detail-bullets`}>
            {entry.bullets.map((bullet) => (
              <div key={bullet} className="detail-bullet" data-testid={`detail-bullet-${bullet.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {bullet}
              </div>
            ))}
          </div>
        ) : null}
        {type === "blog" ? (
          <div className="detail-body" data-testid="blog-detail-body">
            <p>{entry.body}</p>
          </div>
        ) : (
          <div className="detail-body" data-testid={`${type}-detail-body`}>
            <p>
              Looking for a fast next step? Call {BUSINESS.phoneDisplay}, request an instant range,
              or book a free on-site estimate and we’ll shape the scope around your timeline.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const AppRoutes = () => {
  const { data: siteData, isLoading, refetch } = useQuery({
    queryKey: ["site-overview"],
    queryFn: api.getSiteOverview,
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage siteData={siteData} refetchSite={refetch} />} />
        <Route path="/admin" element={<AdminDashboard siteData={siteData} refetchSite={refetch} />} />
        <Route path="/services/:slug" element={<DetailPage siteData={siteData} type="service" />} />
        <Route path="/locations/:slug" element={<DetailPage siteData={siteData} type="location" />} />
        <Route path="/blog/:slug" element={<DetailPage siteData={siteData} type="blog" />} />
        <Route path="*" element={<DetailPage siteData={siteData} type="service" />} />
      </Routes>
    </>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.1,
      smoothWheel: true,
      syncTouch: false,
    });

    let frameId = 0;
    const raf = (time) => {
      lenis.raf(time);
      frameId = requestAnimationFrame(raf);
    };

    frameId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(frameId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App app-shell" data-testid="painting-site-app-shell">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
